import { useTranslation } from 'react-i18next';
import { MoreVertical, Pencil, Trash2, X } from 'lucide-react';
import { Button } from '@repo/ui/src/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@repo/ui/src/components/ui/dropdown-menu';
import type { ScheduleTemplateSummary } from '@repo/shared-types';
import { fmtAppliedDate } from '../../lib/appliedDate';

// Tinted like the existing attention banner (MoveModeHint, same tokens) — a
// template being edited is exactly the kind of thing a teacher must not
// forget they're inside of. Rendered under PageHeader, above MoveModeHint,
// whenever `templateId !== null`.
export function TemplateBanner({
  template, onApply, onRename, onDelete, onExit,
}: {
  template: ScheduleTemplateSummary;
  onApply: () => void;
  onRename: () => void;
  onDelete: () => void;
  onExit: () => void;
}) {
  const { t } = useTranslation('scheduling');

  return (
    <div className="mb-4 rounded-md border border-attention/30 bg-attention-tint px-4 py-3">
      {/* Row 1: header (name + appliedAt) and the action cluster share the top
          row, actions pinned top-right — not beside/below the explanation
          text, which gets its own full-width row underneath. */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <span className="truncate font-semibold text-attention-foreground">{template.name}</span>
          {template.appliedAt ? (
            <span className="shrink-0 text-xs text-attention-foreground/70">
              {t('template.appliedNote', { date: fmtAppliedDate(template.appliedAt) })}
            </span>
          ) : null}
        </div>

        {/* One inseparable row: the three actions must never break between
            each other. The whole group wraps under the header as a single
            unit on narrow screens (this row is flex-wrap; this container
            isn't). Sizing mirrors PageHeader's mobile compaction
            (pageHeaderActionClass): a shorter mobile height than a bare
            sm-size button, touch target satisfied by padding rather than a
            literal 44px box, so the cluster doesn't overpower the header. */}
        <div className="flex shrink-0 items-center gap-[8px] whitespace-nowrap">
          <Button
            type="button"
            size="sm"
            onClick={onApply}
            className="h-[38.4px] sm:h-8"
          >
            {t('template.apply')}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-[38.4px] w-[38.4px] p-0 sm:h-8 sm:w-8"
                aria-label={t('template.menuLabel')}
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={onRename} className="cursor-pointer">
                <Pencil className="mr-2 h-3.5 w-3.5" /> {t('template.rename')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onDelete} className="cursor-pointer text-destructive focus:bg-destructive/10 focus:text-destructive">
                <Trash2 className="mr-2 h-3.5 w-3.5" /> {t('template.delete')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onExit}
            className="h-[38.4px] gap-1.5 sm:h-8"
          >
            <X className="h-3.5 w-3.5" /> {t('template.exit')}
          </Button>
        </div>
      </div>

      {/* Row 2: the explanation, full-width underneath the header row. */}
      <p className="mt-1 text-sm text-attention-foreground">{t('template.liveNote')}</p>
    </div>
  );
}
